<template>
  <b-card>
    <b-overlay :show="isLoading" rounded="sm">
      <template v-if="suggestion">
        <div class="mb-2">
          <b-badge :variant="statusVariant(suggestion.status)" class="mb-1">
            {{ statusLabel(suggestion.status) }}
          </b-badge>
          <h4>
            Матрица <b-badge variant="light-primary" style="font-size: 1rem">{{ suggestion.value }}</b-badge>
            будет добавлена артисту <strong>{{ suggestion.title }}</strong>
          </h4>
          <div class="text-muted">
            Колонка «Матрица» в Notion получит ещё одно значение.
            Создана: {{ formatDateTime(suggestion.created_at) }}
          </div>
        </div>

        <div class="d-flex flex-wrap align-items-center">
          <b-button
              variant="success"
              size="lg"
              class="mr-1 mb-1"
              :disabled="isProcessing || suggestion.status === 'applied'"
              @click="confirm"
          >
            <b-spinner v-if="processingAction === 'confirm'" small class="mr-50" />
            <feather-icon v-else icon="CheckIcon" class="mr-50" />
            Подтвердить
          </b-button>

          <b-button
              variant="outline-secondary"
              class="mr-1 mb-1"
              :disabled="isProcessing || suggestion.status === 'ignored'"
              @click="ignore"
          >
            <b-spinner v-if="processingAction === 'ignore'" small class="mr-50" />
            <feather-icon v-else icon="EyeOffIcon" class="mr-50" />
            Игнорировать
          </b-button>

          <b-button
              variant="outline-danger"
              class="mb-1"
              :disabled="isProcessing"
              @click="remove"
          >
            <b-spinner v-if="processingAction === 'delete'" small class="mr-50" />
            <feather-icon v-else icon="TrashIcon" class="mr-50" />
            Удалить
          </b-button>
        </div>

        <div class="mt-1">
          <b-link :to="{ name: 'content-suggestions' }">← К списку предложек</b-link>
        </div>
      </template>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BCard,
  BOverlay,
  BButton,
  BBadge,
  BSpinner,
  BLink,
} from 'bootstrap-vue'
import { onUnmounted, ref, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import contentStoreModule from '@/views/content/contentStoreModule'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { refreshSuggestionBadge } from '@/views/content/suggestionBadge'

export default {
  components: {
    BCard,
    BOverlay,
    BButton,
    BBadge,
    BSpinner,
    BLink,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-content'
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, contentStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const toast = useToast()

    const suggestionId = ref(Number(router.currentRoute.params.id))
    const suggestion = ref(null)
    const isLoading = ref(false)
    const processingAction = ref(null)
    const isProcessing = computed(() => processingAction.value !== null)

    const statusLabels = { pending: 'Ожидает', applied: 'Применена', ignored: 'Игнорирована' }
    const statusVariants = { pending: 'light-warning', applied: 'light-success', ignored: 'light-secondary' }

    const statusLabel = status => statusLabels[status] || status
    const statusVariant = status => statusVariants[status] || 'light-secondary'

    const formatDateTime = iso => new Date(iso).toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })

    const showToast = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckCircleIcon',
          variant,
        },
      })
    }

    const errorMessage = (error, fallback) => (
      (error.response && error.response.data && error.response.data.message) || fallback
    )

    const loadSuggestion = () => {
      isLoading.value = true
      store.dispatch('app-content/fetchSuggestion', { id: suggestionId.value })
          .then(response => {
            suggestion.value = response.data.data
          })
          .catch(error => {
            showToast(errorMessage(error, 'Предложка не найдена'), 'danger')
            router.push({ name: 'content-suggestions' })
          })
          .finally(() => {
            isLoading.value = false
          })
    }

    // После действия: есть следующая pending-предложка — переходим к ней, иначе в список.
    const goNext = nextId => {
      if (nextId) {
        suggestionId.value = nextId
        router.replace({ name: 'content-suggestion-view', params: { id: nextId } })
        loadSuggestion()
      } else {
        showToast('Предложек больше нет', 'success')
        router.push({ name: 'content-suggestions' })
      }
    }

    const confirm = () => {
      processingAction.value = 'confirm'
      store.dispatch('app-content/confirmSuggestion', { id: suggestionId.value })
          .then(response => {
            showToast('Изменение применено к Notion', 'success')
            refreshSuggestionBadge()
            goNext(response.data.next_id)
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка применения к Notion'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    const ignore = () => {
      processingAction.value = 'ignore'
      store.dispatch('app-content/ignoreSuggestion', { id: suggestionId.value })
          .then(response => {
            showToast('Предложка игнорирована', 'success')
            refreshSuggestionBadge()
            goNext(response.data.next_id)
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка игнорирования'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    const remove = () => {
      processingAction.value = 'delete'
      store.dispatch('app-content/deleteSuggestion', { id: suggestionId.value })
          .then(() => {
            showToast('Предложка удалена', 'success')
            refreshSuggestionBadge()
            router.push({ name: 'content-suggestions' })
          })
          .catch(error => showToast(errorMessage(error, 'Ошибка удаления'), 'danger'))
          .finally(() => {
            processingAction.value = null
          })
    }

    loadSuggestion()

    return {
      suggestion,
      isLoading,
      processingAction,
      isProcessing,
      statusLabel,
      statusVariant,
      formatDateTime,
      confirm,
      ignore,
      remove,
    }
  },
}
</script>
